import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Importazione dei componenti di pagina
import HomePage from '../pages/homepage/homepage';
import Login from '../components/login';
import Registrati from '../components/registrati';
import ResetPassword from '../components/resetPassword';
import Domanda from '../Domanda';
import Risposta from '../Risposta';
import Tabella from '../tabella';
import ListaForm from '../ListaForm';
import ListaFormBackoffice from '../ListaFormBackoffice';
import VisualizzaDownload from '../VisualizzaDownload';
import ListPartner from '../ListPartner';
import ProjectTable from '../ProjectTable';
import SubProjectTable from '../SubProjectTable';
import FormGradimento from '../pages/FormGradimento/formGradimento';
import HomepageValutazioni from '../pages/FormGradimento/homepageValutazioni';
import MonitorValutazioni from '../pages/MonitorValutazioni/monitorValutazioni';
import RiepilogoValutazioneIntermedia from '../pages/FormGradimento/riepilogoValutazioneIntermedia';
import ListaCandidati_Partner from '../pages/ListaCandidati_partner/listaCandidati_partner';
import GestioneSottoProgetti_partner from '../pages/GestioneSottoProgetti_partner/gestioneSottoProgetti_partner';
import MonitorValutazioni_Partner from '../pages/MonitorValutazioni_partner/monitorValutazioni_partner';
import PrivacyPolicy from '../pages/PrivacyPolicy/PrivacyPolicy';
import TermsAndConditions from '../pages/TermsAndConditions/TermsAndConditions';
import VideoTutorials from '../pages/VideoTutorials/VideoTutorials';
import VideoTutorialsAdmin from '../pages/VideoTutorialsAdmin/VideoTutorialsAdmin';



// Componente per route protette che verifica l'autenticazione
const ProtectedRoute = ({ element, allowedRoles }) => {
    const { token, roles, hasAnyRole } = useAuth();

    // Verifica se l'utente è autenticato
    if (!token) {
        return <Navigate to="/login" replace />;
    }

    // Verifica se l'utente ha i permessi necessari
    if (allowedRoles && allowedRoles.length > 0 && !hasAnyRole(allowedRoles)) {
        return <Navigate to="/" replace />;
    }

    // Se l'utente è autenticato e ha i permessi necessari, renderizza il componente
    return element;
};

// Componente per route pubbliche accessibili solo a utenti non autenticati
const PublicRoute = ({ element }) => {
    const { token } = useAuth();

    // Se l'utente è già autenticato, reindirizza alla homepage
    if (token) {
        return <Navigate to="/homepage" replace />;
    }

    return element;
};


const AppRoutes = () => {
    const { roles, hasAnyRole } = useAuth();

    // Route pubbliche accessibili a tutti
    const publicRoutes = [
        { path: '/homepage', element: <HomePage /> },
        { path: '/', element: <HomePage /> },
        { path: '/resetPassword/:token', element: <ResetPassword /> },
        { path: '/download/:formSubmitId/:metaKey', element: <VisualizzaDownload /> }, // Deve essere pubblico per funzionare, non so il perché
        { path: '/formRisposta/:formId', element: <Risposta /> }, // Deve essere pubblico per funzionare, non so il perché
        { path: '/formRisposta/:formId/:userId', element: <Risposta /> }, // Deve essere pubblico per funzionare, non so il perché
        { path: '/privacy-policy', element: <PrivacyPolicy /> },
        { path: '/termini-e-condizioni', element: <TermsAndConditions /> },
        { path: '/videotutorials', element: <VideoTutorials /> },
        { path: '/videotutorials_admin', element: <VideoTutorialsAdmin /> }

    ];

    // Route accessibili solo agli utenti non autenticati
    const unauthenticatedRoutes = [
        { path: '/login', element: <Login /> },
        { path: '/registrami', element: <Registrati /> }
    ];

    // Route accessibili a utenti standard
    const standardUserRoutes = [
        { path: '/form/:formId', element: <Domanda /> },
        { path: '/listaForm', element: <ListaForm /> },
        { path: '/formRisposta/:formId', element: <Risposta /> },
        { path: '/homepageValutazioni', element: <HomepageValutazioni /> },
        { path: '/formGradimento', element: <FormGradimento /> },
        { path: '/riepilogoValutazioneIntermedia', element: <RiepilogoValutazioneIntermedia /> },
    ];

    // Route specifiche per le scuole
    const schoolRoutes = [
        { path: '/form/:formId', element: <Domanda /> },
        { path: '/listaForm', element: <ListaForm /> },
        { path: '/formRisposta/:formId', element: <Risposta /> }
    ];

    // Route specifiche per i partner
    const hostingPartnerRoutes = [
        { path: '/listaCandidati_partner', element: <ListaCandidati_Partner /> },
        { path: '/listaCandidati_partner/:formId', element: <ListaCandidati_Partner /> },
        { path: '/formRisposta/:formId/:userId', element: <Risposta /> },
        { path: '/gestioneSottoProgetti_partner', element: <GestioneSottoProgetti_partner /> },
        { path: '/gestioneSottoProgetti_partner/:idProjectPassato', element: <GestioneSottoProgetti_partner /> },
        { path: '/download/:formSubmitId/:metaKey', element: <VisualizzaDownload /> },
        { path: '/gestioneValutazioni_partner', element: <MonitorValutazioni_Partner /> },
    ];

    // Route specifiche per i moderatori
    const moderatorRoutes = [
        { path: '/form/:formId', element: <Domanda /> },
        { path: '/listaForm', element: <ListaForm /> },
        { path: '/formRisposta/:formId', element: <Risposta /> }
    ];

    // Route accessibili solo agli admin
    const adminRoutes = [
        { path: '/tab', element: <Tabella /> },
        { path: '/tab/:formId', element: <Tabella /> },
        { path: '/formRisposta/:formId/:userId', element: <Risposta /> },
        { path: '/listaFormBackoffice', element: <ListaFormBackoffice /> },
        { path: '/partner', element: <ListPartner /> },
        { path: '/project', element: <ProjectTable /> },
        { path: '/project/:idSubProjectPassato', element: <ProjectTable /> },
        { path: '/subProject', element: <SubProjectTable /> },
        { path: '/subProject/:idProjectPassato', element: <SubProjectTable /> },
        { path: '/download/:formSubmitId/:metaKey', element: <VisualizzaDownload /> },
        { path: '/monitorValutazioni', element: <MonitorValutazioni /> },
    ];

    return (
        <Routes>
            {/* Route pubbliche */}
            {publicRoutes.map((route, index) => (
                <Route key={`public-${index}`} path={route.path} element={route.element} />
            ))}

            {/* Route per utenti non autenticati */}
            {unauthenticatedRoutes.map((route, index) => (
                <Route
                    key={`unauth-${index}`}
                    path={route.path}
                    element={<PublicRoute element={route.element} />}
                />
            ))}

            {/* Route per utenti standard */}
            {standardUserRoutes.map((route, index) => (
                <Route
                    key={`standard-${index}`}
                    path={route.path}
                    element={
                        <ProtectedRoute
                            element={route.element}
                            allowedRoles={["ROLE_USER", "ROLE_ADMIN"]}
                        />
                    }
                />
            ))}

            {/* Route per moderatori */}
            {moderatorRoutes.map((route, index) => (
                <Route
                    key={`moderator-${index}`}
                    path={route.path}
                    element={
                        <ProtectedRoute
                            element={route.element}
                            allowedRoles={["ROLE_MODERATOR", "ROLE_ADMIN"]}
                        />
                    }
                />
            ))}

            {/* Route per scuole */}
            {schoolRoutes.map((route, index) => (
                <Route
                    key={`school-${index}`}
                    path={route.path}
                    element={
                        <ProtectedRoute
                            element={route.element}
                            allowedRoles={["ROLE_SCHOOL", "ROLE_ADMIN"]}
                        />
                    }
                />
            ))}

            {/* Route per partner ospitanti */}
            {hostingPartnerRoutes.map((route, index) => (
                <Route
                    key={`partner-${index}`}
                    path={route.path}
                    element={
                        <ProtectedRoute
                            element={route.element}
                            allowedRoles={["ROLE_HOSTING_PARTNER", "ROLE_ADMIN"]}
                        />
                    }
                />
            ))}
            
            {/* Route per admin */}
            {adminRoutes.map((route, index) => (
                <Route
                    key={`admin-${index}`}
                    path={route.path}
                    element={
                        <ProtectedRoute
                            element={route.element}
                            allowedRoles={["ROLE_ADMIN"]}
                        />
                    }
                />
            ))}

            {/* Cattura tutte le altre route e reindirizza alla homepage */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};


/* Vecchie Routes
const AppRoutes = () => {
    const { token, ruolo } = useAuth();

    return (
        <Routes>

            <Route href="#homepage" path='/homepage' element={<HomePage />}></Route>

            {token == null ? (
                <>
                    <Route href="#login" path='/login' element={<Login />}></Route>
                    <Route href="#registrami" path='/registrami' element={<Registrati />} ></Route>
                </>
            ) : (
                <Route />
            )}
            {token != null && ruolo != null && ruolo != "ROLE_ADMIN" ? (
                <>
                    <Route href="#form" path='/form/:formId' element={<Domanda />}></Route><Route href="#listaForm" path='/listaForm' element={<ListaForm />}></Route>
                </>
            ) : (
                <Route></Route>
            )}
            {ruolo == "ROLE_ADMIN" ? (
                <><Route href="#tab" path='/tab' element={<Tabella />}></Route>
                    <Route href="#tab" path='/tab/:formId' element={<Tabella />}></Route>
                    <Route href="#formRisposta" path='/formRisposta/:formId/:userId' element={<Risposta />}></Route>
                    <Route href="#listaFormBackoffice" path='/listaFormBackoffice' element={<ListaFormBackoffice />}></Route>
                    <Route href="#partner" path='/partner' element={<ListPartner />}></Route>
                    <Route href="#project" path='/project' element={<ProjectTable />}></Route>
                    <Route href="#project" path='/project/:idSubProjectPassato' element={<ProjectTable />}></Route>
                    <Route href="#subProject" path='/subProject' element={<SubProjectTable />}></Route>
                    <Route href="#subProject" path='/subProject/:idProjectPassato' element={<SubProjectTable />}></Route>
                    <Route href="#download" path='/download/:formSubmitId/:metaKey' element={<VisualizzaDownload />}></Route></>
            ) : (
                <Route></Route>
            )}
            <Route href="#formRisposta" path='/formRisposta/:formId' element={<Risposta />}></Route>


            <Route href="#formRisposta" path='/formRisposta/:formId/:userId' element={<Risposta />}></Route>
            <Route href="#resetPassword" path='/resetPassword/:token' element={<ResetPassword />}></Route>
            <Route href="#" path='/' element={<HomePage />}></Route>

            <Route path="*" element={<HomePage />} />

        </Routes>
    );
};*/

export default AppRoutes;